import React, { FC, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { EvTag, EvTitle, TAG_STATUS } from '@evinced-private/ui-common';

import { useDashboardBasePath } from 'src/apps/dashboard/hooks/useDashboardBasePath';
import { filtersToQueryParams } from 'src/apps/dashboard/pages/issues/services/IssuesPageQueryParamsService';
import { UniqueIssuesData } from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';
import { ScoreData } from 'src/apps/dashboard/services/queries/score/helpers/transformResults';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { UNIQUE_QUERY_PARAM } from 'src/common/helpers/QueryParamsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';

import { AccessibilitySummaryCard } from '../../AccessibilitySummaryCard';
import { DISABLED_FEATURE_MESSAGE, INFO_CONTENT } from '../../constants';
import { buildDrilldownLink } from '../../helpers/drilldownLinkHelper';
import { DRILLDOWN_LINK_LABELS } from '../../helpers/drilldownLinkHelper.labels';
import { transformAccessibilityData } from '../../helpers/transformAccessibilityData';
import {
	getNAStatusTag,
	getStatusTag,
	getTrendAriaLabel,
	getTrendTag
} from '../../helpers/uiHelpers';

import './AccessibilitySummary.scss';

interface AccessibilitySummaryProps {
	scoreData?: ScoreData[];
	uniqueIssuesData?: UniqueIssuesData;
	totalIssuesData?: UniqueIssuesData;
	className?: string;
	isUniquenessServiceEnabled?: boolean;
	filters?: IFilterOptions;
	totalIssuesIsUnique?: boolean;
}

export const AccessibilitySummary: FC<AccessibilitySummaryProps> = ({
	scoreData,
	uniqueIssuesData,
	totalIssuesData,
	className = '',
	isUniquenessServiceEnabled = false,
	filters,
	totalIssuesIsUnique = false
}) => {
	const location = useLocation();
	const basePath = useDashboardBasePath();
	const data = useMemo(
		() => transformAccessibilityData(scoreData, uniqueIssuesData, totalIssuesData),
		[scoreData, uniqueIssuesData, totalIssuesData]
	);

	const buildDrilldownUrl = useCallback(
		(isUniqueIssuesTab: boolean, severity?: string): string => {
			const issuesFilters: IFilterOptions = {};
			const {
				unitId: unitIdFilter,
				environment: environmentFilter,
				dateRange: dateRangeFilter
			} = filters ?? {};

			if (Array.isArray(unitIdFilter) && unitIdFilter.length > 0) {
				issuesFilters.unitId = unitIdFilter;
			}

			if (Array.isArray(environmentFilter) && environmentFilter.length > 0) {
				issuesFilters.environment = environmentFilter;
			}

			if (Array.isArray(dateRangeFilter) && dateRangeFilter.length > 0) {
				issuesFilters.dateRange = dateRangeFilter;
			}

			if (severity) {
				issuesFilters.severities = [{ value: severity, label: severity }];
			}

			const url = new URL(RoutesHelper.getIssuesPath(basePath), window.location.origin);
			const params = filtersToQueryParams(issuesFilters);
			if (isUniqueIssuesTab) {
				params.set(UNIQUE_QUERY_PARAM, 'true');
			}
			url.search = params.toString();

			return `${url.pathname}${url.search}`;
		},
		[filters, basePath]
	);

	return (
		<div
			className={`accessibility-summary ${className}`}
			aria-label="Accessibility validation results summary"
		>
			<AccessibilitySummaryCard
				type="score"
				id="accessibility-score"
				title="Accessibility score"
				className="accessibility-score-card"
				infoContent={INFO_CONTENT.ACCESSIBILITY_SCORE}
				ariaLabel={
					data.groupedScore.score === null
						? 'Score: No data available'
						: `Score: ${data.groupedScore.score} out of 100`
				}
				score={data.groupedScore.score}
				maxScore={100}
				statusTag={
					data.groupedScore.score === null
						? getNAStatusTag()
						: getStatusTag(data.groupedScore.status)
				}
			/>

			{/* Divider after Accessibility Score */}
			<div className="metric-divider" aria-hidden="true" />

			{/* Unique Issues Group */}
			{isUniquenessServiceEnabled ? (
				<div className="metric-group">
					<AccessibilitySummaryCard
						type="metric"
						id="unique-issues"
						title="Unique issues"
						className="unique-issues-card"
						infoContent={INFO_CONTENT.UNIQUE_ISSUES}
						ariaLabel={`Unique issues: ${data.uniqueIssues.allIssues.value}`}
						value={data.uniqueIssues.allIssues.value}
						trendTag={getTrendTag(data.uniqueIssues.allIssues)}
						trendAriaLabel={getTrendAriaLabel(data.uniqueIssues.allIssues)}
						drilldownLink={buildDrilldownLink(
							DRILLDOWN_LINK_LABELS.UNIQUE_ISSUES,
							true,
							buildDrilldownUrl,
							location.pathname
						)}
					/>

					<AccessibilitySummaryCard
						type="metric"
						id="unique-critical-issues"
						title="Unique critical issues"
						className="unique-critical-issues-card"
						infoContent={INFO_CONTENT.UNIQUE_CRITICAL_ISSUES}
						ariaLabel={`Unique critical issues: ${data.uniqueIssues.critical.value}`}
						value={data.uniqueIssues.critical.value}
						trendTag={getTrendTag(data.uniqueIssues.critical)}
						trendAriaLabel={getTrendAriaLabel(data.uniqueIssues.critical)}
						drilldownLink={buildDrilldownLink(
							DRILLDOWN_LINK_LABELS.UNIQUE_CRITICAL_ISSUES,
							true,
							buildDrilldownUrl,
							location.pathname,
							DRILLDOWN_LINK_LABELS.CRITICAL_SEVERITY
						)}
					/>
				</div>
			) : (
				<div className="metric-group unique-issues-disabled">
					<div
						className="summary-card unique-issues-card disabled-card"
						role="group"
						aria-labelledby="unique-issues-disabled-title"
					>
						<div className="card-header">
							<EvTitle
								id="unique-issues-disabled-title"
								titleText="Unique issues"
								headingLevel={3}
								className="card-title"
								MoreInfoContent={<div>{INFO_CONTENT.UNIQUE_ISSUES}</div>}
							/>
						</div>
						<div className="card-content disabled-content">
							<p className="disabled-message">{DISABLED_FEATURE_MESSAGE}</p>
							<EvTag status={TAG_STATUS.INFO} text="N/A" aria-label="Status: Not available" />
						</div>
					</div>
				</div>
			)}

			{data.totalIssues && (
				<>
					{/* Divider after Unique Critical Issues */}
					<div className="metric-divider" aria-hidden="true" />

					{/* Total Issues Group */}
					<div className="metric-group">
						<AccessibilitySummaryCard
							type="metric"
							id="total-issues"
							title="Total issues"
							className="total-issues-card"
							infoContent={INFO_CONTENT.TOTAL_ISSUES}
							ariaLabel={`Total issues: ${data.totalIssues.allIssues.value}`}
							value={data.totalIssues.allIssues.value}
							trendTag={getTrendTag(data.totalIssues.allIssues)}
							trendAriaLabel={getTrendAriaLabel(data.totalIssues.allIssues)}
							drilldownLink={buildDrilldownLink(
								DRILLDOWN_LINK_LABELS.TOTAL_ISSUES,
								totalIssuesIsUnique,
								buildDrilldownUrl,
								location.pathname
							)}
						/>

						<AccessibilitySummaryCard
							type="metric"
							id="total-critical-issues"
							title="Total critical issues"
							className="total-critical-issues-card"
							infoContent={INFO_CONTENT.TOTAL_CRITICAL_ISSUES}
							ariaLabel={`Total critical issues: ${data.totalIssues.critical.value}`}
							value={data.totalIssues.critical.value}
							trendTag={getTrendTag(data.totalIssues.critical)}
							trendAriaLabel={getTrendAriaLabel(data.totalIssues.critical)}
							drilldownLink={buildDrilldownLink(
								DRILLDOWN_LINK_LABELS.CRITICAL_ISSUES,
								totalIssuesIsUnique,
								buildDrilldownUrl,
								location.pathname,
								DRILLDOWN_LINK_LABELS.CRITICAL_SEVERITY
							)}
						/>
					</div>
				</>
			)}
		</div>
	);
};
